import { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { addDays, startOfDay, compareAsc, formatISO, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { useAtom } from 'jotai'

import { DateType, TripType } from '../../../components/transpais/enums'
import { BASE_URL } from '../../../utilities/constants'
import {
  bookingSessionOrigin,
  bookingSessionDestination,
  bookingSessionDepartureDate,
  bookingSessionReturningDate,
  bookingSessionTripConfig,
  bookingSessionCountFares,
  bookingSessionFares,
  bookingSessionDepartureBlockedTickets,
  originDefault,
  settingsConfig,
  bookingSessionTimeoutDeadline
} from '../../../utilities/atoms'

import { handleApiError, reportError } from '../../../helpers/errorHandler'
import { isSingleTrip } from '../../../helpers/tripHelpers'
import transpais from '../../../services/transpais'

import { usePinpad } from '../../../contexts/pinpad.context'
import { useModalView } from '../../../contexts/modalview.context'
import { useModal } from '../../../contexts/modal.context'
import { useTimer } from '../../../contexts/timer.context'
import { clearBookingTimer } from '../../../helpers/bookingTimer'

import PlacesView from '../partials/PlacesView'
import CalendarView from '../partials/CalendarView'

export function useBookingSearch() {
  const navigate = useNavigate()
  const { showModalView, setupModalView, destroyModalView } = useModalView()
  const { showModal, hideModal, setupModal } = useModal()
  // Pinpad loading (inicialización) no debe bloquear la búsqueda de viajes.
  // Se ignora 'loading' para no mostrar Loader global en esta vista.
  usePinpad() // sólo para garantizar que el contexto esté inicializado, sin usar loading
  const { hideTimer } = useTimer()

  // Data hooks
  const [origins, setOrigins] = useState(null)
  const [destinationsApi, setDestinationsApi] = useState(null)
  const [destinations, setDestinations] = useState(null)
  const [localLoading, setLocalLoading] = useState(false)
  const [fares, setFares] = useAtom(bookingSessionFares)
  const [count, setCount] = useAtom(bookingSessionCountFares)
  const [selectedType, setSelectedType] = useState(0) // 0: Sencillo, 1: Redondo
  const [canContinue, setCanContinue] = useState(false)
  const [defaultOrigin, ] = useAtom(originDefault)
  const [settings] = useAtom(settingsConfig) 
  const [, setTimeoutDeadline] = useAtom(bookingSessionTimeoutDeadline)

  const [, setBookingDepartureBlockedTickets] = useAtom(bookingSessionDepartureBlockedTickets)
  const [bookingOrigin, setBookingOrigin] = useAtom(bookingSessionOrigin)
  const [bookingDestination, setBookingDestination] = useAtom(bookingSessionDestination)
  const [bookingTripConfig, setBookingTripConfig] = useAtom(bookingSessionTripConfig)
  const [bookingDepartureDate, setBookingDepartureDate] = useAtom(bookingSessionDepartureDate)
  const [bookingReturningDate, setBookingReturningDate] = useAtom(bookingSessionReturningDate)

  const [departureDate, setDepartureDate] = useState(null)
  const [returningDate, setReturningDate] = useState(null)

  const allowedPassengerTypes = ['adulto', 'senectud', 'profesor', 'estudiante']
  const fareList = ["adulto", "menor", "senectud", "mascota", "preferente", "profesor", "estudiante"]
  const allowedTypes = [TripType.single, TripType.round]
  const days = useRef(settings && settings.days ? settings.days : 30)

  // Data fetching
  const loadOrigins = async () => {
    setLocalLoading(true)
    try {
      const response = await transpais.origins()
      setDestinationsApi(response.data.all)
      setOrigins(response.data.origins)
    } catch (error) {
      const { userMessage, errorData } = handleApiError(error, { action: 'loadOrigins' })
      reportError(error, errorData)
      console.error('Error cargando orígenes:', userMessage)
    } finally {
      setLocalLoading(false)
    }
  }

  const loadFares = async () => {
    try {
      const response = await transpais.fares()
      const faresData = response.data.fares
      const fareDisclaimers = response.data.disclaimer

      let availableFares = faresData
        .filter((fare) => (
          fareList.includes(fare.indexableName) &&
          !fare.disabled &&
          !fare.deleted
        ))
        .map((fare) => ({
          ...fare,
          disclaimer: fareDisclaimers[fare._id] || null,
        }))
        .sort((fare1, fare2) => (fare1.ord || 0) - (fare2.ord || 0))
      setFares(availableFares)
    } catch (error) {
      const { userMessage, errorData } = handleApiError(error, { action: 'loadFares' })
      reportError(error, errorData)
      console.error('Error cargando tarifas:', userMessage)
    }
  }

  const showDestinations = (bookingOrigin) => {
    if (!bookingOrigin || !origins) return
    setLocalLoading(true)
    try {
      const matchedDestinations = destinationsApi.filter((origin) =>
        bookingOrigin.to.includes(origin._id)
      )
      setDestinations(matchedDestinations)
    } catch (error) {
      const { userMessage, errorData } = handleApiError(error, { action: 'showDestinations' })
      reportError(error, errorData)
      console.log('Error cargando destinos:', userMessage)
    } finally {
      setLocalLoading(false)
    }
  }

  // Dates logic
  const setDates = (date, type) => {
    if (type === DateType.departure) {
      if (bookingReturningDate && compareAsc(date, parseISO(bookingReturningDate)) > 0) {
        setReturningDate(date)
        setBookingReturningDate(formatISO(date))
      }
      setDepartureDate(date)
      setBookingDepartureDate(formatISO(date))
      if (allowedTypes[selectedType] === TripType.round) {
        destroyModalView()
        setTimeout(() => {
          setupModalView({
            title: 'Selecciona tus fechas',
            content: (
              <CalendarView
                count={30}
                onChange={setDates}
                departure={formatISO(date)}
                returning={bookingReturningDate}
                tripType={TripType.round}
                type={DateType.returning}
                onFinish={handleContinueSearch}
              />
            ),
          })
          showModalView()
        }, 600)
      }
    } else if (type === DateType.returning) {
      if (bookingDepartureDate && compareAsc(parseISO(bookingDepartureDate), date) > 0) {
        setDepartureDate(date)
        setBookingDepartureDate(formatISO(date))
      }
      setReturningDate(date)
      setBookingReturningDate(formatISO(date))
      setBookingTripConfig((prevConfig) => ({
        ...prevConfig,
        type: TripType.round,
      }))
    }
  }

  // Handlers
  const handleExit = () => {
    navigate(`${BASE_URL}/`)
  }

  const handleChangeTripType = (index) => {
    setSelectedType(index)
  }

  const handleSelectOrigin = () => {
    if (!origins) {
      loadOrigins()
    } else {
      setupModalView({
        title: 'Selecciona un origen',
        content: <PlacesView items={origins} onChange={(place) => {
          setBookingOrigin(place)
          setBookingDestination(null)
          destroyModalView()
        }} />,
      })
      showModalView()
    }
  }

  const handleSelectDestination = () => {
    showDestinations(bookingOrigin)
  }

  const handleValidateRoundTrip = () => {
    setLocalLoading(true)
    const existsDestination = origins && bookingDestination && origins.some((item) => item._id === bookingDestination._id)
    if (!existsDestination) {
      setLocalLoading(false)
      setSelectedType(0)
      setupModal({
        title: 'Atención',
        content: <p>Lo sentimos, por el momento no contamos con viajes de regreso para tu viaje.</p>,
        actions: {
          positive: {
            title: 'Aceptar',
            onClick: () => {
              handleSearch()
              hideModal()
            },
          },
        },
      })
      showModal()
    } else {
      setLocalLoading(false)
      handleSearch()
    }
  }

  const handleSearch = () => {
    if (canContinue) {
      Sentry.setContext('Search', {
        origen: bookingOrigin,
        destino: bookingDestination,
        tipo: allowedTypes[selectedType],
        ida: bookingDepartureDate,
        regreso: bookingReturningDate,
      })
      navigate(`${BASE_URL}/booking/trips`)
    }
  }

  const handleContinueSearch = () => {
    destroyModalView()
    if (!hasSelectedFares) return

    if (bookingTripConfig.type === TripType.round) {
      handleValidateRoundTrip()
    } else if (isSingleTrip(bookingTripConfig)) {
      handleSearch()
    }
  }

  const onIncrement = (fareId) => {
    setCount((prevCount) => {
      const totalCount = Object.values(prevCount).reduce((sum, value) => sum + value, 0)
      if (totalCount < 5) {
        return {
          ...prevCount,
          [fareId]: (prevCount[fareId] || 0) + 1,
        }
      }
      return prevCount
    })
  }

  const onDecrement = (fareId) => {
    setCount((prevCount) => {
      const newCount = { ...prevCount }
      if (newCount[fareId] > 0) {
        newCount[fareId] -= 1
      }
      if (newCount[fareId] === 0) {
        delete newCount[fareId]
      }
      return newCount
    })
  }

  const hasSelectedFares = fares && fares.some(
    (fare) =>
      allowedPassengerTypes.includes(fare.indexableName) && count[fare._id] > 0
  )

  // Effects
  useEffect(() => {
    // Limpiar cualquier timer activo previo al iniciar nueva búsqueda
    clearBookingTimer({
      hideTimer,
      setDeadline: setTimeoutDeadline
    })

    if (bookingDepartureDate === '') {
      setBookingDepartureDate(formatISO(startOfDay(new Date())))
    }
    if (bookingReturningDate === '') {
      setBookingReturningDate(formatISO(startOfDay(addDays(new Date(), 1))))
    }
    loadOrigins()
    loadFares()
    setBookingDepartureBlockedTickets([])
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (fares && fares.length > 0 && Object.values(count).every((v) => v === 0)) {
      const adultoFare = fares.find((fare) => fare.name.toLowerCase() === 'adulto')
      if (adultoFare) {
        setCount((prevCount) => ({
          ...prevCount,
          [adultoFare._id]: 1,
        }))
      }
    }
    // eslint-disable-next-line
  }, [fares])

  useEffect(() => {
    if (bookingOrigin && bookingDestination && Object.keys(bookingDestination).length > 0) {
      setBookingTripConfig({
        type: allowedTypes[selectedType],
        redondo: false,
      })
      if (selectedType === 1) {
        if (compareAsc(parseISO(bookingDepartureDate), parseISO(bookingReturningDate)) <= 0) {
          setCanContinue(true)
        }
      } else {
        setCanContinue(true)
      }
    } else {
      setCanContinue(false)
    }
    // eslint-disable-next-line
  }, [bookingOrigin, bookingDestination, bookingDepartureDate, bookingReturningDate, selectedType])

  useEffect(() => {
    if (destinations) {
      setupModalView({
        title: 'Selecciona un destino',
        content: <PlacesView items={destinations} onChange={(place) => {
          setBookingDestination(place)
          destroyModalView()
        }} />,
      })
      showModalView()
    }
    // eslint-disable-next-line
  }, [destinations])

  useEffect(() => {
    if (departureDate) {
      setBookingDepartureDate(formatISO(departureDate))
    }
    if (returningDate) {
      setBookingReturningDate(formatISO(returningDate))
    }
    // eslint-disable-next-line
  }, [departureDate])

  useEffect(() => {
    if (defaultOrigin && origins && Array.isArray(origins)) {
      const _origin = origins.find((origin) => origin._id === defaultOrigin)
      setBookingOrigin(_origin)
    }
    // eslint-disable-next-line
  }, [origins])

  // Update days ref when settings.days changes
  useEffect(() => {
    if (settings && settings.days) {
      days.current = settings.days
    }
  }, [settings])

  return {
  // loading: se omite para no bloquear la UI por pinpad
    localLoading,
    fares,
    count,
    selectedType,
    canContinue,
    bookingOrigin,
    bookingDestination,
    bookingDepartureDate,
    bookingReturningDate,
    handleExit,
    handleChangeTripType,
    handleSelectOrigin,
    handleSelectDestination,
    handleContinueSearch,
    setDates,
    onIncrement,
    onDecrement,
    hasSelectedFares,
    setupModalView,
    showModalView,
    days,
    setSelectedType,
  }
}
